import countriesData from './data/countries.json';
import { applyFxShareDefault, getFxShareDefault } from './fxShare';
import type { CountryState, SliderKey, SliderValues, YearlySliders } from './types';

export const BASELINE_YEAR = 2026;
export const HISTORICAL_YEARS = [2022, 2023, 2024, 2025];
export const PROJECTION_YEARS = [2026, 2027, 2028, 2029, 2030, 2031];
export const HORIZON_YEARS = PROJECTION_YEARS.length;
const SLIDER_KEYS: SliderKey[] = [
  'realGdpGrowth', 'realInterestRate', 'primaryBalance',
  'realFxAppreciation', 'fcuShare',
];

export type RawCountry = Omit<CountryState, 'defaults' | 'yearlyDefaults'> & {
  defaults: { [K in keyof SliderValues]: number | null };
  yearlyDefaults?: { [K in keyof YearlySliders]: Array<number | null> };
};

export interface WeoMetadata {
  source_vintage: string;
  extracted_from: string;
  extracted_date: string;
  country_count: number;
  baseline_year: number;
  historical_window: number[];
  projection_years: number[];
  horizon_years: number;
  selectable_country_count?: number;
  source_country_count?: number;
  source_sha256?: string;
  source_publication_date?: string;
  source_update_date?: string;
  retrieved_at?: string;
}

const data = countriesData as unknown as {
  _meta: WeoMetadata;
  countries: RawCountry[];
};
export const WEO_METADATA = data._meta;
export const WEO_VINTAGE_DATE = WEO_METADATA.source_vintage.replace(/^IMF WEO /, '');
export const WEO_DATASET_URL = 'https://data.imf.org/en/datasets/IMF.RES:WEO';

function sameYears(actual: number[], expected: number[]): boolean {
  return Array.isArray(actual) && actual.length === expected.length &&
    actual.every((year, index) => year === expected[index]);
}

if (
  WEO_METADATA.baseline_year !== BASELINE_YEAR ||
  WEO_METADATA.horizon_years !== HORIZON_YEARS ||
  !sameYears(WEO_METADATA.historical_window, HISTORICAL_YEARS) ||
  !sameYears(WEO_METADATA.projection_years, PROJECTION_YEARS) ||
  !WEO_METADATA.source_vintage || !WEO_METADATA.extracted_from ||
  WEO_METADATA.country_count !== data.countries.length
) {
  throw new Error('WEO metadata does not match the supported model window or extract.');
}

const finite = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/** One eligibility rule for the selector, initial load, country changes and Reset. */
export function isSelectableCountry(country: RawCountry): country is CountryState {
  const yearly = country.yearlyDefaults;
  if (!yearly || country.baselineYear !== BASELINE_YEAR ||
      !finite(country.startingDebtPct) || country.startingDebtPct <= 0) return false;
  if (!Array.isArray(country.historical) ||
      country.historical.length !== HISTORICAL_YEARS.length ||
      !country.historical.every((point, index) =>
        point.year === HISTORICAL_YEARS[index] && finite(point.debtPct) && point.debtPct >= 0) ||
      country.historical[3].debtPct !== country.startingDebtPct) return false;
  if (!Array.isArray(country.baselineProjection) ||
      country.baselineProjection.length !== HORIZON_YEARS ||
      !country.baselineProjection.every((point, index) =>
        point.year === PROJECTION_YEARS[index] && finite(point.debtPct) &&
        (index === HORIZON_YEARS - 1 ? point.debtPct >= 0 : point.debtPct > 0))) return false;
  return SLIDER_KEYS.every(key => {
    const values = yearly[key];
    return Array.isArray(values) && values.length === HORIZON_YEARS &&
      values.every(finite) && finite(country.defaults[key]) &&
      country.defaults[key] === values[0] &&
      (key !== 'realInterestRate' || values.every(value => value >= -20 && value <= 50)) &&
      (key !== 'realGdpGrowth' || values.every(value => value > -100));
  });
}

/** Keep the WEO source boundary separate from the provenance-aware DSA transform. */
export function loadCountries(rawCountries: RawCountry[]): CountryState[] {
  const seen = new Set<string>();
  for (const country of rawCountries) {
    if (!/^[a-z]{3}$/.test(country.iso) || seen.has(country.iso)) {
      throw new Error(`Invalid or duplicate WEO country code: ${country.iso}`);
    }
    seen.add(country.iso);
  }
  return rawCountries.filter(isSelectableCountry).map(country =>
    applyFxShareDefault(country, getFxShareDefault(country.iso)));
}

// Keep reproducible source rows ISO-sorted while preserving alphabetical UI order.
export const SELECTABLE_COUNTRIES = loadCountries(data.countries)
  .sort((left, right) => left.name.localeCompare(right.name, 'en'));
if (SELECTABLE_COUNTRIES.length === 0 ||
    (WEO_METADATA.selectable_country_count !== undefined &&
     WEO_METADATA.selectable_country_count !== SELECTABLE_COUNTRIES.length)) {
  throw new Error('WEO selectable-country metadata does not match the production loader.');
}
export const FX_SHARE_COVERAGE = {
  observed: SELECTABLE_COUNTRIES.filter(country => country.fxShareDefault?.kind === 'observed').length,
  missing: SELECTABLE_COUNTRIES.filter(country => country.fxShareDefault?.kind === 'fallback').length,
};

/** No URL persistence exists; a removed or unknown selection uses the first eligible economy. */
export function getCountry(iso: string): CountryState {
  return SELECTABLE_COUNTRIES.find(country => country.iso === iso) ?? SELECTABLE_COUNTRIES[0];
}

/** Fresh arrays prevent a scenario edit from changing Reset defaults. */
export function buildInitialSliders(country: CountryState): YearlySliders {
  if (!country.yearlyDefaults) throw new Error(`Missing yearly defaults: ${country.iso}`);
  return {
    realGdpGrowth: [...country.yearlyDefaults.realGdpGrowth],
    realInterestRate: [...country.yearlyDefaults.realInterestRate],
    primaryBalance: [...country.yearlyDefaults.primaryBalance],
    realFxAppreciation: [...country.yearlyDefaults.realFxAppreciation],
    fcuShare: [...country.yearlyDefaults.fcuShare],
  };
}
